/**
 * Human-call workspace: the shared data + edit layer behind the Call Script.
 *
 * Backs the Call Script COLUMN of the Review view, beside the transcript and the case
 * Output sidebar. Extracted from the standalone Human Call Script tab (BV-1575) when that
 * tab was folded into Review, which is why the data layer lives apart from its
 * presentational `CallScriptPane`.
 *
 * The live working set is the human CALL's `output_data` — but only while a call is live.
 * Before/after a call `callDetails` (and so the call's output) does not resolve at all;
 * in that read-only state the panes and the disposition engine read the CASE's
 * `output_data` instead (`readBaseline`).
 *
 * Local edits flow through `useCallDataEditor.handleDataChange` (in-memory state — the
 * source of truth the panes read from), and are persisted to the server with a SILENT,
 * debounced `useUpdateCall` POST of `{ output_data }` (error toast only; no per-keystroke
 * success toast, and no null-reset of the working output).
 *
 * Disposition is recomputed CLIENT-SIDE and instantly via `useCommittedDisposition` (the
 * shared commit-gated snapshot + engine wiring also used by the Review tab in
 * DefaultCaseDetailsPage), so the script statuses and board icons react with no network
 * round-trip. The persisted case disposition is reconciled on finalize by firing
 * `ACTION_CALCULATE_DISPOSITION` (owned by `useHumanCallPersistence`).
 *
 * A client-only "heard" state (`useHeardFields`, localStorage, keys + timestamps only —
 * never values) lets the agent flag free-entry fields they heard on the recording but
 * haven't transcribed yet. It is gated on `callActive` and threaded into both models.
 */
import type { CallInfo } from '@compass/client/types/call';
import type { CaseRecord } from '@compass/client/types/case_record';
import { useCallback, useMemo, useRef, useState } from 'react';

import { useActiveScript } from '@/hooks/useActiveScript';
import { useCallDataEditor } from '@/hooks/useCallDataEditor';
import { useCommittedDisposition } from '@/hooks/useCommittedDisposition';
import { useEngineAgentId } from '@/hooks/useEngineAgentId';
import { useSchema } from '@/hooks/useSchema';
import { useStore } from '@/store/index';
import type { FieldType, FormField } from '@/types/formSchema';
import { getValueAtPath } from '@/utils/auditCorrections';
import { caseSchemaVersion } from '@/utils/caseSchemaVersion';
import type { ScriptEntityLayout, ScriptFilterMode } from '@/utils/humanCall/scriptModel';
import type { BoardSection, ScriptModel } from '@/utils/humanCallModel';
import { buildFieldBoard, buildScriptModel } from '@/utils/humanCallModel';
import { setOutputAtPath } from '@/utils/outputPath';
import { collectPhoneFieldErrors, getPhoneFieldError } from '@/utils/phone';

import { useHeardFields } from './heard';
import { useHumanCallPersistence } from './useHumanCallPersistence';

/**
 * Workflows whose script stays one card per code instead of the multi-code grid
 * (BV-2742). medbhobv keeps a `medbv_` and a `bho_` copy of every entity array, and the
 * disposition engine (run as unifiedmedbv) reports their required fields without the
 * prefix — so the grid would mark the wrong cells. Drop it from here once the engine
 * understands medbhobv.
 */
const PER_CODE_SCRIPT_WORKFLOW_TYPES = new Set(['medbhobv']);

function scriptLayoutFor(workflowType: string | undefined): ScriptEntityLayout {
  return workflowType && PER_CODE_SCRIPT_WORKFLOW_TYPES.has(workflowType) ? 'per-code' : 'matrix';
}

export interface UseHumanCallWorkspaceArgs {
  caseId: string;
  caseDetails: CaseRecord;
  /** The live in-progress human call whose output_data is the working set (when editable). */
  callDetails?: CallInfo;
  /** When false, the workspace is read-only: data from the case, no edits/persistence. */
  editable: boolean;
  /** True only while the call is live (CALL_STATUS_IN_PROGRESS). A strict subset of editable. */
  callActive: boolean;
}

export interface HumanCallWorkspace {
  /** Guided-script model (units, current step, catch-all fields) for `ScriptFlow`. */
  model: ScriptModel;
  /** "All fields" board sections (every output leaf, grouped) for `FieldBoard`. */
  board: BoardSection[];
  heardAt: (key: string) => number | undefined;
  /** Toggle a field's heard mark. A no-op unless the call is live. */
  toggleHeard: (key: string) => void;
  onFieldChange: (key: string, value: unknown) => void;
  /**
   * Apply several field writes as ONE output revision (BV-1575). Used by the script's
   * unit-level clone, which copies a question's answers across entity instances: looping
   * `onFieldChange` would work (each call re-reads the pending output) but would schedule
   * a save per field and commit the disposition mid-way through a partially-applied set.
   * This reduces every entry into a single output, saves once, and commits once.
   */
  onFieldsChange: (entries: ReadonlyArray<{ key: string; value: unknown }>) => void;
  /**
   * Advance the disposition snapshot (fired on blur for text fields).
   *
   * `key` is the field that just lost focus, when the caller knows it. Passing it
   * re-validates that field before the snapshot advances, so a rep who types an
   * invalid number and tabs away sees the message even though the value's own save
   * was already refused on change (LA-7358). Omitting it keeps the old
   * commit-only behaviour.
   */
  handleOutputCommit: (key?: string) => void;
  /**
   * Per-field validation errors, keyed by the same bracket-indexed path the script and
   * board use for their rows — `drug[0].fax_number` (BV-2129, LA-7358). Live:
   * recomputed on every write, so a message appears and clears as the rep types.
   *
   * Coverage is phone/fax format only — that is the whole client-side validation surface
   * in the app (`getPhoneFieldError` / `collectPhoneFieldErrors`). Required-field
   * emptiness is NOT an error here; it is surfaced by the disposition engine as the
   * script's count pills and the Missed filter.
   */
  fieldErrors: Record<string, string>;
  /**
   * Validate the WHOLE working output and publish the result to {@link fieldErrors}
   * (BV-2129). Returns the map, so a caller can act on it in the same tick.
   *
   * Needed because the live per-write validation only knows fields touched this session,
   * while Save has to answer for values the rep never opened. The page's own
   * `handleOutputSaveClick` cannot do this job here: it validates ITS `useCallDataEditor`
   * instance, and the script layout writes through this hook's separate one — two
   * independent useState drafts, sharing no backing store (the human call opts out of
   * localStorage for PHI). So Save validated a copy that never saw the board's edits and
   * always found it clean.
   */
  validateAllFields: () => Record<string, string>;
  /**
   * The All / Missed / Required narrowing, shared by the Output board and the Call
   * Script (LA-7358).
   *
   * View state, deliberately placed in the data hook: the board and the script are
   * siblings under `HumanCallWorkspaceProvider` with no other common owner, and the
   * two panes are meant to narrow together — the script used to carry its own
   * duplicate control, so the same case could be filtered two different ways at once.
   * The board owns the CONTROL; this is just where the value lives so both can read it.
   */
  filterMode: ScriptFilterMode;
  setFilterMode: (mode: ScriptFilterMode) => void;
  /**
   * The latest request, from the Output board, to scroll the Call Script to a field.
   * View state kept here for the same reason as {@link filterMode}: the two panes have
   * no other common owner. `seq` makes a second click on the same row a new request.
   */
  scriptRevealRequest: { key: string; seq: number } | null;
  revealInScript: (key: string) => void;
  /** Call start in epoch ms, for "heard · mm:ss" labels. */
  callStartMs?: number;
  isSaving: boolean;
}

export function useHumanCallWorkspace(args: UseHumanCallWorkspaceArgs): HumanCallWorkspace {
  const { caseId, caseDetails, callDetails, editable, callActive } = args;
  // Read both records through one destructure each. Every `caseDetails?.x` in a hook body
  // is its own branch for the `complexity` lint (which runs on .ts, though not on the
  // .tsx this was extracted from), and the repeated optional chaining added nothing.
  const {
    workflow_id: workflowId,
    workflow_type: workflowType,
    disposition: caseDisposition,
    input_data: caseInputData,
    output_data: caseOutput,
  } = caseDetails ?? ({} as Partial<CaseRecord>);
  const {
    id: callId,
    output_data: callOutputData,
    heard_markers: heardMarkers,
    started_at: callStartedAt,
    created_at: callCreatedAt,
  } = callDetails ?? ({} as Partial<CallInfo>);
  const connectedMonotonicMs = useStore(state =>
    callId ? state.connectedCallAnchors[callId] : undefined,
  );

  // Persistence layer (both debounced saves + finalize + beforeunload + unmount).
  const { scheduleOutputSave, peekPendingOutput, scheduleHeardSave, isSaving } =
    useHumanCallPersistence({ caseId, callId });

  // Agent id for the engine's program-flag resolution — keyed on the case's workflow,
  // mirroring bvagent (see `useEngineAgentId`).
  const agentId = useEngineAgentId(workflowId);
  const inputData = useMemo(() => caseInputData?.data ?? {}, [caseInputData?.data]);

  // `callOutputData` (the ACTIVE call's output_data, destructured above) is the source of
  // truth for the script WHILE A CALL IS LIVE — call-level, NOT the case's accumulated
  // output: the script, board, and disposition all read from THIS call's captured output,
  // and edits persist back to this same call's output_data.
  // NOTE: CallInfo.output_data is the flat value map directly — unlike CaseRecord, which
  // nests it under `.data`.

  // `callDetails` only resolves while a call is LIVE, so before/after a call the tab has
  // no call-level output at all. It is still visible for the whole human-call phase, and
  // in that read-only state the CASE's output_data is the right baseline. Without this
  // fallback the panes rendered every field empty and — worse — the disposition engine ran
  // against no output, so every required field counted as missing (required == missing).
  const caseOutputData = caseOutput?.data as Record<string, unknown> | undefined;
  /**
   * Call output while a call is live; the case's accumulated output otherwise.
   *
   * Keyed on `callDetails` presence, NOT on `callOutputData ?? caseOutputData`: a freshly
   * dialed call legitimately has empty output, and falling through to the case there would
   * prefill the script with values this call never captured — changing live-call behaviour.
   * With a call present the baseline stays strictly call-level, empty or not.
   */
  const readBaseline = callDetails ? callOutputData : caseOutputData;

  // --- Edit state + persistence (shared by both panes) --------------------
  // `useCallDataEditor` owns the in-memory working output. We deliberately do
  // NOT use its `handleCallSave` here: that path shows a success toast on every
  // flush and null-resets `editedCallData.output`. Instead we persist silently
  // below.
  //
  // `persistToLocalStorage: false` — the working output holds field VALUES
  // (PHI), so it must NEVER be mirrored to localStorage (HIPAA). With the flag
  // off the hook keeps `editedCallData` purely in memory and performs zero
  // localStorage reads/writes; the live working set survives only for the
  // lifetime of this mounted tab, and is persisted to the server (not the
  // browser) via the debounced POST below.
  const { editedCallData, handleDataChange } = useCallDataEditor({
    caseId,
    selectedCallId: editable ? callId : undefined,
    callDetails: editable ? callDetails : null,
    outputData: callOutputData,
    persistToLocalStorage: false,
  });

  // The working output is the edited call output, falling back to `readBaseline` — this
  // call's stored output_data while a call is live, else the case's. Edits still go only to
  // the live call; the case output is a READ baseline for the no-call state. Since we no
  // longer call `handleCallSave` (the only thing that null-reset `editedCallData.output`
  // mid-session), no local mirror is needed: the editor state stays populated for this call.
  const workingOutput = useMemo<Record<string, unknown>>(
    () =>
      (editable ? (editedCallData.output ?? readBaseline ?? {}) : (readBaseline ?? {})) as Record<
        string,
        unknown
      >,
    [editable, editedCallData.output, readBaseline],
  );
  // Mirror the freshest working output into a ref so `onFieldChange` can read it
  // without depending on `workingOutput` (which changes every keystroke). This
  // keeps the handler identity stable across keystrokes without changing behavior.
  const workingOutputRef = useRef(workingOutput);
  workingOutputRef.current = workingOutput;

  /**
   * The output a write handler just produced, before React has re-rendered (LA-7358).
   *
   * `workingOutputRef` is only assigned during render, so it lags any write made in the
   * SAME tick. That is not hypothetical: the script's "Accept <extracted value>" button
   * calls `onFieldChange` and `onFieldCommit` back to back in one click handler, so the
   * commit's re-validation read the PRE-Accept value, found it fine, and deleted the
   * error the change had just raised — the field took a bad value with no message and no
   * red border on it.
   *
   * `basedOn` is the render snapshot the write was derived from, which is what makes the
   * entry self-expiring: once the render catches up, `workingOutputRef` no longer points
   * at it and the pending tree is ignored. So a server baseline arriving later is never
   * shadowed by a stale local tree. Same trick, and the same reason, as
   * `useCommittedDisposition`'s `effectiveRef`.
   */
  const pendingWriteRef = useRef<{
    tree: Record<string, unknown>;
    basedOn: Record<string, unknown>;
  } | null>(null);

  /** Record a write made this tick, so the rest of the tick can see it. */
  const notePendingWrite = useCallback((tree: Record<string, unknown>) => {
    pendingWriteRef.current = { tree, basedOn: workingOutputRef.current };
  }, []);

  /** The freshest output there is: a same-tick write if one is pending, else the render. */
  const readFreshestOutput = useCallback((): Record<string, unknown> => {
    const pending = pendingWriteRef.current;
    return pending && pending.basedOn === workingOutputRef.current
      ? pending.tree
      : workingOutputRef.current;
  }, []);

  // --- Schema groups (same derivation as CaseSummaryView) -----------------
  // Output only: the caller renders the input side itself.
  const { outputGroups } = useSchema(workflowId, caseSchemaVersion(caseDetails));

  // --- Active call script -------------------------------------------------
  const { data: scriptData } = useActiveScript(workflowId);
  const script = scriptData?.script;

  // Call start (for "heard · mm:ss" labels). Prefer the actual start; fall back to
  // creation. Undefined when neither is set — the labels fall back to a clock time.
  const callStartMs = useMemo(() => {
    const t = callStartedAt ?? callCreatedAt;
    const ms = t ? Date.parse(t) : NaN;
    return Number.isNaN(ms) ? undefined : ms;
  }, [callStartedAt, callCreatedAt]);

  // --- Committed-snapshot disposition (shared with the Review tab) ---------
  // `useCommittedDisposition` owns the commit-gated snapshot + engine wiring so
  // the disposition re-evaluates on field COMMITs only — never on in-flight
  // keystrokes (the `useDispositionHelper` contract) — both here and in
  // DefaultCaseDetailsPage. This tab is always in edit mode: the read baseline is THIS
  // CALL's output_data (call-level — the disposition is evaluated against the call's
  // captured values, not the case's), the live working set is the call's edited output,
  // and `handleOutputCommit` advances the snapshot on commit. The disposition SCENARIO is
  // still the case workflow's (`workflowType`); when the engine doesn't support it we fall
  // back to the persisted case disposition (rather than collapsing to 0/0).
  const { handleOutputCommit, requiredFields, missingFields } = useCommittedDisposition({
    workflowType,
    // Call output while a call is live, else the case's — see `readBaseline`. Passing the
    // (undefined) call output in the no-call state made the engine see NO output, so every
    // required field counted as missing.
    baselineOutput: readBaseline,
    editedOutput: editable ? editedCallData.output : undefined,
    isEditing: editable,
    fallbackDisposition: caseDisposition,
    // Feed the engine a combined context: case input (seed) values overlaid with
    // the call's committed output. Output wins on collisions; input fills gaps.
    inputData,
    // The engine resolves program-flag-gated required fields (COB, AFP, tier exception,
    // REMS, …) solely from the case workflow's agent id (BI-1795).
    agentId,
    // Surface the workflow's always-collected fields (engine's OPTIONAL_FIELDS) in the
    // script so reps capture them every call. They merge into `requiredFields` only —
    // empty ones show as required without blocking step advancement.
    alwaysIncludeOptionalFields: true,
  });

  // --- Commit-type classification (for the script path) -------------------
  // The board accordion already fires `onFieldBlur` correctly (on blur for text;
  // synchronously after change for instant-commit controls). The script step
  // (ScriptStep via ScalarFieldEditable) has no standalone blur callback at the
  // ScriptStep level, so we classify by field type:
  // instant-commit controls snapshot immediately; typed/text fields wait for the
  // next commit (e.g. a board blur), matching the hook contract.
  // Field types whose change IS a complete value (a single discrete choice), so
  // editing them from a script step commits the disposition snapshot immediately.
  // NUMBER/FLOAT/TEXT are deliberately EXCLUDED: they are typed digit-by-digit, so
  // committing on each keystroke makes the engine treat a partial number (e.g. "1")
  // as filled and drop it from the missing set mid-typing. Those commit on BLUR
  // (or Enter) instead — see ScalarFieldEditable's `onFieldBlur` wired through
  // ScriptFieldRow/ScriptStep to `onFieldCommit`.
  const COMMIT_FIELD_TYPES = useMemo(
    () => new Set<FieldType>(['select', 'multi_select', 'date', 'dob', 'checkbox', 'radio']),
    [],
  );
  // Map a normalized output key (array indices stripped, e.g.
  // `procedure.coverage.coverage_status`) to its schema field type, so the
  // script path can tell commit-type changes from in-flight text.
  const keyToFieldType = useMemo(() => {
    const map: Record<string, FieldType> = {};
    const walk = (field: FormField, basePath: string) => {
      const path = basePath ? `${basePath}.${field.id}` : field.id;
      if (field.type === 'array' || field.type === 'object') {
        field.fields?.forEach(sub => walk(sub, path));
        return;
      }
      map[path] = field.type;
    };
    outputGroups.forEach(group => group.fields.forEach(f => walk(f, '')));
    return map;
  }, [outputGroups]);
  const isCommitTypeKey = useCallback(
    (key: string) => {
      // Strip array indices to match the schema walk's path form.
      const normalized = key.replace(/\[\d+\]/g, '');
      const type = keyToFieldType[normalized];
      return type ? COMMIT_FIELD_TYPES.has(type) : false;
    },
    [keyToFieldType, COMMIT_FIELD_TYPES],
  );

  // --- Shared edit handler ------------------------------------------------
  // Keys edited during THIS session (so the model can mark them "captured").
  const sessionEditedKeys = useRef<Set<string>>(new Set());

  // --- Client-only "heard" marks (localStorage; keys + timestamps only) ----
  // Lets the agent flag free-entry fields they heard on the recording but haven't
  // transcribed yet. Stores NO values (HIPAA). Keyed per call id. User-driven
  // changes flow into the persistence hook's debounced heard save.
  const heard = useHeardFields(callId, {
    connectedMonotonicMs,
    initialMarkers: heardMarkers,
    onUserChange: scheduleHeardSave,
  });

  /** Single-field edit (from a script step). Clone-and-set at the concrete key. */
  // Live validation for the script's own write path (BV-2129). The script persists
  // through a silent debounced useUpdateCall with no save button, so there is no
  // save-time gate to hang validation off the way the Output pane does — it has to run
  // on write or not at all, which is why a rep typing a malformed fax into the script
  // previously saw nothing.
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Shared All / Missed / Required narrowing — see `filterMode` on the interface.
  // Opens on `all`: reps need the whole script in view (BI-1670).
  const [filterMode, setFilterMode] = useState<ScriptFilterMode>('all');

  // Board row click → scroll the script — see `scriptRevealRequest` on the interface.
  const [scriptRevealRequest, setScriptRevealRequest] = useState<{
    key: string;
    seq: number;
  } | null>(null);
  const revealInScript = useCallback(
    (key: string) => setScriptRevealRequest(prev => ({ key, seq: (prev?.seq ?? 0) + 1 })),
    [],
  );

  // Returns the message so the caller can gate on it in the same tick — `fieldErrors`
  // is state and won't have updated yet when `onFieldChange` needs the answer.
  const validateField = useCallback(
    (key: string, value: unknown): string | null => {
      const message = getPhoneFieldError(outputGroups, key, value);
      setFieldErrors(prev => {
        if (message) {
          if (prev[key] === message) return prev;
          return { ...prev, [key]: message };
        }
        if (!(key in prev)) return prev;
        const rest = { ...prev };
        delete rest[key];
        return rest;
      });
      return message;
    },
    [outputGroups],
  );

  const validateAllFields = useCallback(() => {
    const errors = collectPhoneFieldErrors(
      outputGroups,
      // The ON-SCREEN tree, for the same reason as `validateFieldAt`: invalid values
      // are never scheduled, so `peekPendingOutput()` is precisely the tree with the
      // failures filtered out — validating it would report clean and wave the save
      // (and the Confirm dialog) straight through (LA-7358).
      readFreshestOutput(),
      // The script and board key their rows `drug[0].fax_number`; the default dot
      // notation would produce a non-empty map that no row could render.
      'bracket',
    );
    setFieldErrors(errors);
    return errors;
  }, [outputGroups, readFreshestOutput]);

  /**
   * Re-validate one field against the freshest working output (LA-7358).
   *
   * Used on blur, where the caller has the key but not the value.
   *
   * Reads the ON-SCREEN tree, NOT `peekPendingOutput()`. The pending tree is the last
   * value that was *scheduled* for save, and an invalid value is deliberately never
   * scheduled — so consulting it here would re-validate the previous, valid value and
   * clear the very error blur is supposed to surface. `workingOutputRef` always holds
   * what the rep typed, because `onFieldChange` calls `handleDataChange` for valid and
   * invalid edits alike.
   *
   * `getValueAtPath` (not `getNestedValue`) because row keys are bracket-indexed —
   * `getNestedValue` splits on "." and would read a literal `"drug[0]"` key.
   */
  const validateFieldAt = useCallback(
    (key: string) => {
      validateField(key, getValueAtPath(readFreshestOutput(), key));
    },
    [validateField, readFreshestOutput],
  );

  /**
   * For every field currently failing validation, the value the server last held —
   * captured at the moment it went invalid (LA-7358).
   *
   * A save is a whole-`output_data` POST cloned from the working tree, so refusing to
   * SCHEDULE an invalid field's own save is not enough on its own: the next valid edit
   * to any other field would clone a tree that still contains it and push it anyway.
   * {@link withoutInvalidFields} rewinds those keys in the payload, so an invalid value
   * lives only on screen until the rep fixes it.
   *
   * A ref, not state: it is read inside the write handlers and must never cost a render.
   */
  const lastValidByKey = useRef<Map<string, unknown>>(new Map());

  /** Rewind every currently-invalid field in `tree` to its last server-safe value. */
  const withoutInvalidFields = useCallback((tree: Record<string, unknown>) => {
    let out = tree;
    for (const [key, value] of lastValidByKey.current) out = setOutputAtPath(out, key, value);
    return out;
  }, []);

  /**
   * Blur handler for the script/board rows: validate the field that lost focus,
   * then advance the disposition snapshot.
   *
   * The snapshot advances even when the field is invalid, and deliberately so:
   * skipping the commit would leave the engine stale for every other field the rep
   * touched in the same pass.
   *
   * Note what that means. `useCommittedDisposition` snapshots `editedCallData.output`
   * — the ON-SCREEN tree — so a malformed value the server write refused is still
   * scored by the engine, and counts as that field being filled. The two are
   * answering different questions (is this worth persisting? vs what has the rep
   * captured so far?), and the engine's answer is provisional until the value is
   * corrected. Do not "fix" this by withholding the commit: the disposition would
   * then lag every other field edited alongside the bad one.
   */
  const handleFieldCommit = useCallback(
    (key?: string) => {
      if (key) validateFieldAt(key);
      handleOutputCommit();
    },
    [validateFieldAt, handleOutputCommit],
  );

  const onFieldChange = useCallback(
    (key: string, value: unknown) => {
      if (!editable) return;
      // Validate BEFORE persisting, and gate the save on the result (LA-7358).
      // `validateField` returns the message rather than only publishing it to state,
      // because `fieldErrors` won't have re-rendered yet at this point.
      const message = validateField(key, value);
      // Clone from the freshest output, not the memoized render snapshot. If two
      // edits land before React re-renders (cascade/mirror fields, rapid commits),
      // `workingOutputRef.current` is still pre-first-edit — cloning from it would
      // drop the first field. The persistence hook's pending output holds the last
      // applied output synchronously; it's null only after a flush, when
      // `workingOutputRef.current` is already current. Reading via the ref (mirrored
      // each render) lets this callback stay stable across keystrokes.
      const base = peekPendingOutput() ?? workingOutputRef.current;
      const next = setOutputAtPath(base, key, value);
      sessionEditedKeys.current.add(key);
      // The on-screen working set ALWAYS takes the edit, valid or not — the rep has to
      // see what they typed to be able to correct it. Only the server write is gated.
      handleDataChange('output', next);
      // Publish it for the rest of THIS tick too: a caller that changes and commits in
      // one handler (the script's Accept button) must not re-validate the old value.
      notePendingWrite(next);
      if (message) {
        // Remember what the server last held for this field, so a save triggered by a
        // DIFFERENT field cannot carry the invalid value along in its cloned tree.
        // Captured from `base` (pre-edit) and only on the transition into invalid, so
        // repeated keystrokes don't overwrite it with earlier bad input.
        if (!lastValidByKey.current.has(key)) {
          lastValidByKey.current.set(key, getValueAtPath(base, key));
        }
        // No `scheduleOutputSave`, and no commit: the disposition engine scores the
        // committed output, which this value never entered.
        return;
      }
      lastValidByKey.current.delete(key);
      scheduleOutputSave(withoutInvalidFields(next));
      // Commit-type controls (select/date/number/...) commit on change — snapshot
      // for the engine immediately. Text fields commit on blur via the script
      // step's `onFieldCommit` (ScalarFieldEditable's `onFieldBlur` wired through
      // ScriptFieldRow/ScriptStep), matching the board.
      if (isCommitTypeKey(key)) handleOutputCommit();
    },
    [
      editable,
      peekPendingOutput,
      scheduleOutputSave,
      handleDataChange,
      isCommitTypeKey,
      handleOutputCommit,
      validateField,
      withoutInvalidFields,
      notePendingWrite,
    ],
  );

  // Batched sibling of `onFieldChange`. Same base-reading rule (freshest pending output,
  // else the render mirror) so it composes with in-flight keystrokes; one save, one commit.
  const onFieldsChange = useCallback(
    (entries: ReadonlyArray<{ key: string; value: unknown }>) => {
      if (!editable || entries.length === 0) return;
      const base = peekPendingOutput() ?? workingOutputRef.current;
      let next = base;
      for (const { key, value } of entries) {
        next = setOutputAtPath(next, key, value);
        sessionEditedKeys.current.add(key);
      }
      // Clone sources are existing captured values, so the entries themselves are not
      // re-validated here — but the tree they are cloned into may still carry an
      // unrelated field the rep has left invalid, which must not ride along (LA-7358).
      scheduleOutputSave(withoutInvalidFields(next));
      handleDataChange('output', next);
      notePendingWrite(next);
      // Always commit: a clone writes committed values, not in-progress typing, so the
      // engine should re-score immediately regardless of the fields' control types.
      handleOutputCommit();
    },
    [
      editable,
      peekPendingOutput,
      scheduleOutputSave,
      handleDataChange,
      handleOutputCommit,
      withoutInvalidFields,
      notePendingWrite,
    ],
  );

  // --- Script model -------------------------------------------------------
  const scriptLayout = scriptLayoutFor(workflowType);
  const model = useMemo(
    () =>
      buildScriptModel({
        script,
        outputSchemaGroups: outputGroups,
        outputData: workingOutput,
        inputData,
        missingFields,
        requiredFields,
        heardKeys: heard.heardKeys,
        sessionEditedKeys: sessionEditedKeys.current,
        layout: scriptLayout,
        workflowType,
      }),
    [
      script,
      outputGroups,
      workingOutput,
      inputData,
      missingFields,
      requiredFields,
      heard.heardKeys,
      scriptLayout,
      workflowType,
    ],
  );

  // Toggle a field's "heard" mark — gated on `callActive` (only while the call is
  // live; post-call edits fill fields inline instead of marking them heard).
  // Destructure the stable `toggleHeard` so the callback below depends on it directly
  // rather than the whole `heard` object — `heard`'s identity changes on every mark,
  // which would give `toggleHeard` a new identity and re-render the memoized panes
  // during the live-call interaction the memo is meant to keep cheap.
  const markHeard = heard.toggleHeard;
  const toggleHeard = useCallback(
    (key: string) => {
      if (callActive) markHeard(key);
    },
    [callActive, markHeard],
  );

  // --- "All fields" board model ------------------------------------------
  // Every output leaf field grouped by schema group — the same inputs the script model
  // uses, so the two panes can never disagree about a field's status (BV-1575).
  const board = useMemo(
    () =>
      buildFieldBoard({
        outputSchemaGroups: outputGroups,
        outputData: workingOutput,
        inputData,
        missingFields,
        requiredFields,
        heardKeys: heard.heardKeys,
        sessionEditedKeys: sessionEditedKeys.current,
      }),
    [outputGroups, workingOutput, inputData, missingFields, requiredFields, heard.heardKeys],
  );

  return {
    model,
    board,
    heardAt: heard.heardAt,
    toggleHeard,
    onFieldChange,
    onFieldsChange,
    // The blur-validating wrapper, not the raw engine commit: every consumer of this
    // is a field row's blur handler, and blur is the last chance to tell a rep their
    // number is malformed before they move on (LA-7358).
    handleOutputCommit: handleFieldCommit,
    fieldErrors,
    validateAllFields,
    filterMode,
    setFilterMode,
    scriptRevealRequest,
    revealInScript,
    callStartMs,
    isSaving,
  };
}
